/**
 * "Set up with Claude Code": the one-paste handoff (migration 070).
 *
 * Action first (owner feedback 2026-09-27, "way too much text and unclear
 * actions"): two equal tabs, "Sign in with browser (recommended)" — three
 * steps, the command and one primary "Copy command" button — and "Use an API
 * key" for scripts, CI and agents that can't open a browser. What the agent
 * does and what needs a person follow as two short lines (the full lists sit
 * behind one disclosure); a shared `.mcp.json` sits behind "Other ways to
 * connect". Shown on AI agent access
 * and, compact (no lists), on the first onboarding screen and the Install
 * step next to the manual path.
 *
 * T097: the browser command has no credential in it. `claude mcp login`
 * opens the browser, the person signs in here and approves the agent on the
 * consent screen (screens/OAuthConsent.tsx). The API key — an `fxa_` agent
 * token created here and shown once, inside the command — is for clients
 * that cannot sign in through a browser.
 *
 * #948: agent access needs the current Partner Terms. While they are owed,
 * both tabs (whose commands would fail) are replaced by the "accept the
 * Terms" state, and the key form refuses to create one.
 */
import { useEffect, useRef, useState } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import type { AgentScope, AgentTokenCreated } from '../api/types';
import { PARTNER_TERMS_SETTINGS_PATH } from '../legal/current';
import { useTermsReview } from '../legal/TermsGate';
import { Link } from '../router';
import { useSites } from '../state/sites';
import { CodeBlock, Disclosure, ErrorNotice, Field, Segmented, Tabs } from '../ui/kit';
import { useMutation } from '../ui/hooks';
import {
  AFTER_A_WINDOW,
  AGENT_DOES,
  AGENT_WILL,
  HUMAN_ONLY,
  INTERACTIVE_TERMINAL_NOTE,
  NEEDS_YOU,
  TOKEN_ENV_VAR,
  WINDOW_NOTE,
  claudeCodeCommand,
  claudeCodeOAuthCommand,
  mcpJsonConfig,
  mcpJsonOAuthConfig,
} from './handoff';

export const STEPS = ['Copy the command', 'Paste it in a terminal at your website’s repo', 'Sign in when your browser opens'] as const;

/** The two short lines, from the same groups as the full lists and the server parity test. */
export const DOES_LINE = AGENT_DOES.map((d) => d.short).join(', ');
export const NEEDS_YOU_LINE = NEEDS_YOU.map((g) => g.short).join(', ');

/** The changes owners are told about at once, with Undo (migration 096). */
export function WindowList() {
  return (
    <div className="stack-sm">
      <h3 className="small-head">Live at once, with an Undo for owners</h3>
      <ul className="ticks">
        {AFTER_A_WINDOW.map((g) => (
          <li key={g.short}>{g.full}</li>
        ))}
      </ul>
      <p className="small muted">{WINDOW_NOTE}</p>
    </div>
  );
}

/** The full lists: behind "What an agent can and can't do" here, in the open on the docs page. */
function Lists() {
  return (
    <div className="stack">
    <div className="grid-2 agent-lists">
      <div className="stack-sm">
        <h3 className="small-head">The agent will</h3>
        <ul className="ticks">
          {AGENT_WILL.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
      <div className="stack-sm">
        <h3 className="small-head">Only a person can</h3>
        <ul className="locks">
          {HUMAN_ONLY.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
    </div>
    <WindowList />
    </div>
  );
}

/** The primary action: one prominent button, and a live region that says it worked. */
function CopyCommand({ command, done = 'Copied. Now paste it in a terminal at your website’s repo.' }: { command: string; done?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <div className="copy-row">
      <button
        type="button"
        className="btn btn-primary btn-lg"
        onClick={async () => {
          window.clearTimeout(timer.current);
          try {
            await navigator.clipboard.writeText(command);
            setState('copied');
            timer.current = window.setTimeout(() => setState('idle'), 4000);
          } catch {
            setState('failed');
          }
        }}
      >
        {state === 'copied' ? 'Copied' : 'Copy command'}
      </button>
      <span className="small" role="status" aria-live="polite">
        {state === 'copied' ? done : null}
        {state === 'failed' ? 'Couldn’t copy. Select the command and copy it yourself.' : null}
      </span>
    </div>
  );
}

/** #948/#963: the command would fail at the consent screen, so say what unblocks it instead. */
function TermsNeeded() {
  const { role } = useSession();
  const review = useTermsReview();
  const canAccept = can(role, 'acceptTerms');
  return (
    <div className="notice warn terms-needed" role="status">
      <div className="stack-sm" style={{ gap: 2 }}>
        <strong>An owner or admin needs to accept the updated Partner Terms</strong>
        <span className="small">
          {canAccept ? (
            'Until then an agent can’t connect.'
          ) : (
            <>
              Until then an agent can’t connect. Ask one to accept them under{' '}
              <Link to={PARTNER_TERMS_SETTINGS_PATH}>Organisation → Partner Terms</Link>.
            </>
          )}
        </span>
      </div>
      {canAccept ? (
        <button type="button" className="btn btn-primary" onClick={review.open}>
          Review and accept
        </button>
      ) : null}
    </div>
  );
}

export type ConnectMethod = 'browser' | 'key';

/** `INTERACTIVE_TERMINAL_NOTE`, with the command set as code. */
export function InteractiveTerminalNote() {
  const command = 'claude mcp login';
  return (
    <>
      <code>{command}</code>
      {INTERACTIVE_TERMINAL_NOTE.slice(command.length)}
    </>
  );
}

export function AgentHandoff({ compact = false, onCreated }: { compact?: boolean; onCreated?: (t: AgentTokenCreated) => void }) {
  const { role, termsRequired } = useSession();
  const allowed = can(role, 'agentTokens');
  const [method, setMethod] = useState<ConnectMethod>('browser');
  const command = claudeCodeOAuthCommand();

  const browser = (
    <div className="stack">
      <ol className="agent-steps">
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
      <CodeBlock code={command} label="Claude Code setup command" copy={false} />
      <CopyCommand command={command} />
      <p className="small muted">
        {compact ? (
          // The Install step and the first setup screen (live run 2026-09-27).
          <>
            <InteractiveTerminalNote />{' '}
          </>
        ) : (
          'Needs an interactive terminal. '
        )}
        Didn’t open a browser? Run <code>/mcp</code> in Claude Code. Access lasts up to 30 days and can be
        revoked{' '}
        {compact ? (
          <>
            on <Link to="/agent">AI agent access</Link>
          </>
        ) : (
          'below'
        )}
        .
      </p>
      {!allowed ? <p className="notice">An owner, admin or developer in your organisation approves the sign-in.</p> : null}
    </div>
  );

  const key = allowed ? (
    <TokenCommand onCreated={onCreated} compact={compact} />
  ) : (
    <p className="notice">An owner, admin or developer in your organisation creates API keys.</p>
  );

  return (
    <section className="panel agent-card" aria-labelledby="agent-handoff-title">
      <div className="panel-pad stack">
        <h2 id="agent-handoff-title">Set up with Claude Code</h2>
        {termsRequired ? (
          <TermsNeeded />
        ) : (
          <Tabs
            label="How the agent connects"
            value={method}
            onChange={setMethod}
            tabs={[
              {
                value: 'browser',
                label: (
                  <>
                    Sign in with browser <span className="tab-note">(recommended)</span>
                  </>
                ),
                panel: browser,
              },
              { value: 'key', label: 'Use an API key', panel: key },
            ]}
          />
        )}

        {compact ? null : (
          <div className="agent-summary stack-sm">
            <p className="small">
              <strong>Does:</strong> {DOES_LINE}.
            </p>
            <p className="small">
              <strong>Needs you:</strong> {NEEDS_YOU_LINE}.
            </p>
            <Disclosure label="What an agent can and can’t do">
              <Lists />
            </Disclosure>
          </div>
        )}

        <Disclosure label="Other ways to connect" className="other-ways">
          <div className="stack-sm">
            <h3 className="small-head">Another MCP client, or a shared .mcp.json</h3>
            <p className="small muted">Any MCP client that supports sign-in needs only the address. Nothing in this file is secret.</p>
            <CodeBlock caption=".mcp.json" code={mcpJsonOAuthConfig()} />
          </div>
        </Disclosure>
      </div>
    </section>
  );
}

/** "Use an API key": an `fxa_` token created here and shown once, inside the command. */
function TokenCommand({ onCreated, compact }: { onCreated?: (t: AgentTokenCreated) => void; compact: boolean }) {
  const { api, termsRequired } = useSession();
  const { current } = useSites();
  const [scope, setScope] = useState<'config' | 'read'>('config');
  const [days, setDays] = useState('7');
  const [sites, setSites] = useState<'all' | 'current'>('all');
  const [created, setCreated] = useState<AgentTokenCreated | null>(null);
  const mint = useMutation(async () => {
    const scopes: AgentScope[] = scope === 'config' ? ['read', 'config'] : ['read'];
    const token = await api.createAgentToken({
      label: `API key · ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      scopes,
      site_keys: sites === 'current' && current ? [current.live.site_key] : null,
      expires_in_days: Number(days),
    });
    setCreated(token);
    onCreated?.(token);
    return token;
  });

  if (created) {
    const cmd = claudeCodeCommand(created.token);
    return (
      <div className="stack">
        <div className="notice warn" role="status">
          <span>
            <strong>Copy it now: shown once, stored nowhere.</strong> FinderlyFix keeps only a fingerprint, so this key can’t be shown again.
            Don’t commit it or paste it into chat. It expires{' '}
            {new Date(created.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}; revoke it any time{' '}
            {compact ? (
              <>
                on <Link to="/agent">AI agent access</Link>
              </>
            ) : (
              'below'
            )}
            .
          </span>
        </div>
        <CodeBlock code={cmd} label="Claude Code API key command" copy={false} />
        <CopyCommand command={cmd} />
        <Disclosure label="Use it from a shared .mcp.json instead">
          <div className="stack-sm">
            <p className="small muted">
              Put the key in <code>{TOKEN_ENV_VAR}</code> in each developer’s own shell or secret manager, never in the file:
            </p>
            <CodeBlock caption=".mcp.json (API key)" code={mcpJsonConfig()} />
          </div>
        </Disclosure>
      </div>
    );
  }
  return (
    <div className="stack">
      <p className="small muted">For scripts, CI and agents that can’t open a browser. The key is shown once, inside the command.</p>
      <div className="grid-3 agent-options">
        <div className="field">
          <span className="field-title" aria-hidden="true">What it may do</span>
          <Segmented
            label="What it may do"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'config', label: 'Read and set up' },
              { value: 'read', label: 'Read only' },
            ]}
          />
        </div>
        <Field label="Expires after">
          {(p) => (
            <select {...p} className="input" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="1">1 day</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
            </select>
          )}
        </Field>
        {current ? (
          <Field label="Sites">
            {(p) => (
              <select {...p} className="input" value={sites} onChange={(e) => setSites(e.target.value as 'all' | 'current')}>
                <option value="all">All sites</option>
                <option value="current">Only {current.live.label}</option>
              </select>
            )}
          </Field>
        ) : null}
      </div>
      {termsRequired ? (
        <p className="notice warn small" role="status">
          API keys need the current Partner Terms. An owner or admin accepts them first.
        </p>
      ) : null}
      <ErrorNotice error={mint.error} />
      <div>
        <button type="button" className="btn btn-primary btn-lg" disabled={mint.pending || termsRequired} onClick={() => void mint.run()}>
          {mint.pending ? 'Creating…' : 'Create API key'}
        </button>
      </div>
    </div>
  );
}

export { Lists as AgentLists };
